/**
 * R2VaultStorageProvider.ts — Cloudflare R2 implementation of VaultStorageProvider.
 *
 * Wraps the FileStorageService (which calls /api/files/*) to provide the
 * VaultStorageProvider interface used by vaultStorage.
 *
 * This module auto-registers itself as the primary provider when imported.
 * Import it once at app startup (bootstrap or main.tsx) to activate R2 for all
 * vault operations.
 *
 * Key mapping: VaultStorage keys (e.g. "vault/42/originals/abc.pdf") are stored
 * as the `originalName` field in the file_objects table so they can be looked
 * up by prefix when listing.
 */

import type {
  StorageListOptions,
  StorageListResult,
  StorageObject,
  StorageUploadOptions,
  VaultStorageProvider,
} from './VaultStorage';
import { vaultStorage } from './VaultStorage';
import * as FileStorageService from '@/services/fileStorage/FileStorageService';
import type { FileRecord } from '@/services/fileStorage/FileStorageService';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const SIGNED_URL_TTL = 3600; // 1 hour

function recordToStorageObject(record: FileRecord, url: string): StorageObject {
  return {
    key:              record.originalName ?? record.storageKey,
    contentType:      record.mimeType,
    sizeBytes:        record.sizeBytes,
    createdAt:        record.createdAt,
    updatedAt:        record.lastAccessedAt,
    url,
    expiresInSeconds: SIGNED_URL_TTL,
    metadata:         {
      r2FileId:     String(record.id),
      storageKey:   record.storageKey,
      compressed:   String(record.compressed),
      deduped:      String(record.deduped),
    },
  };
}

// ─── Provider ─────────────────────────────────────────────────────────────────

export class R2VaultStorageProvider implements VaultStorageProvider {
  readonly id   = 'r2';
  readonly name = 'Cloudflare R2';
  get available() { return true; }

  async upload(key: string, blob: Blob, options?: StorageUploadOptions): Promise<StorageObject> {
    const mimeType = options?.contentType ?? blob.type ?? 'application/octet-stream';
    // Use key as the filename so it can be looked up later
    const record = await FileStorageService.uploadFile(blob, key, mimeType);
    const { url } = await FileStorageService.getSignedUrl(record.id, SIGNED_URL_TTL);
    return recordToStorageObject(record, url);
  }

  async download(key: string): Promise<Blob | null> {
    try {
      const files = await FileStorageService.listFiles(200);
      const match = files.find(f => f.originalName === key || f.storageKey === key);
      if (!match) return null;
      const { url } = await FileStorageService.getSignedUrl(match.id, 300);
      const res = await fetch(url);
      if (!res.ok) return null;
      return res.blob();
    } catch {
      return null;
    }
  }

  async stat(key: string): Promise<StorageObject | null> {
    try {
      const files = await FileStorageService.listFiles(200);
      const match = files.find(f => f.originalName === key || f.storageKey === key);
      if (!match) return null;
      const { url } = await FileStorageService.getSignedUrl(match.id, SIGNED_URL_TTL);
      return recordToStorageObject(match, url);
    } catch {
      return null;
    }
  }

  async getUrl(key: string, expiresInSeconds = SIGNED_URL_TTL): Promise<string | null> {
    try {
      const files = await FileStorageService.listFiles(200);
      const match = files.find(f => f.originalName === key || f.storageKey === key);
      if (!match) return null;
      const { url } = await FileStorageService.getSignedUrl(match.id, expiresInSeconds);
      return url;
    } catch {
      return null;
    }
  }

  async delete(key: string): Promise<void> {
    const files = await FileStorageService.listFiles(200);
    const match = files.find(f => f.originalName === key || f.storageKey === key);
    if (match) await FileStorageService.deleteFile(match.id);
  }

  async list(options?: StorageListOptions): Promise<StorageListResult> {
    const limit  = options?.limit ?? 50;
    const files  = await FileStorageService.listFiles(limit);
    const filtered = options?.prefix
      ? files.filter(f => (f.originalName ?? f.storageKey).startsWith(options.prefix!))
      : files;

    const objects: StorageObject[] = await Promise.all(
      filtered.map(async (f) => {
        const { url } = await FileStorageService.getSignedUrl(f.id, SIGNED_URL_TTL);
        return recordToStorageObject(f, url);
      }),
    );

    return { objects, nextCursor: null, hasMore: false };
  }

  async move(fromKey: string, toKey: string): Promise<StorageObject> {
    const blob = await this.download(fromKey);
    if (!blob) throw new Error(`[R2VaultStorageProvider] move: source not found: ${fromKey}`);
    const obj = await this.upload(toKey, blob);
    await this.delete(fromKey).catch(() => {});
    return obj;
  }
}

// ─── Auto-register as primary vault storage provider ─────────────────────────

vaultStorage.register(new R2VaultStorageProvider(), true);
